/** Display names and badge tones for every enum the API returns. */
export type Tone = 'green' | 'orange' | 'sky' | 'warn' | 'red' | 'ink' | 'outline' | '';

export const PHASES = [
  'schematic_design',
  'design_development',
  'construction_documents',
  'bidding',
  'construction_administration',
  'closeout',
] as const;
export type Phase = (typeof PHASES)[number];

export const PHASE_LABEL: Record<string, string> = {
  schematic_design: 'Schematic Design',
  design_development: 'Design Development',
  construction_documents: 'Construction Documents',
  bidding: 'Bidding',
  construction_administration: 'Construction Admin',
  closeout: 'Closeout',
};
export const PHASE_SHORT: Record<string, string> = {
  schematic_design: 'SD',
  design_development: 'DD',
  construction_documents: 'CD',
  bidding: 'Bid',
  construction_administration: 'CA',
  closeout: 'Closeout',
};

export const DEAL_STATUS: Record<string, [string, Tone]> = {
  open: ['Open', 'sky'],
  won: ['Won', 'green'],
  lost: ['Lost', 'red'],
  on_hold: ['On hold', 'warn'],
};

export const SPEC_STATUS: Record<string, [string, Tone]> = {
  not_specified: ['Not specified', 'outline'],
  competing: ['Competing', 'warn'],
  approved_equal: ['Approved equal', 'sky'],
  specified: ['Specified', 'green'],
};

export const PRODUCT_LINE: Record<string, string> = { panel: 'Panels', screen: 'Screens', flow: 'Flow Panels', mobile: 'Mobile Screens', both: 'Panels and screens' };

/**
 * Registration states, for Circdal users only. Agency reps and principals never see
 * registration: the API does not send it to them (Trent, 2026-10-17).
 */
export const REGISTRATION: Record<string, [string, Tone]> = {
  none: ['Not registered', 'outline'],
  pending: ['Registration pending', 'warn'],
  approved: ['Protected', 'green'],
  rejected: ['Not approved', 'red'],
  expired: ['Expired', 'red'],
  released: ['Released', 'outline'],
};

export const PARTY_ROLE: Record<string, string> = {
  architect: 'Architect',
  interior_designer: 'Interior designer',
  general_contractor: 'General contractor',
  bidding_gc: 'Bidding GC',
  owner: 'Owner',
  installer: 'Installer',
  other: 'Other',
};

export const ACCOUNT_TYPE: Record<string, string> = {
  design_firm: 'Design firm',
  general_contractor: 'General contractor',
  owner: 'Owner',
  installer: 'Installer',
  rep_agency: 'Rep agency',
  internal: 'Circdal',
  other: 'Other',
};

export const ROLE: Record<string, string> = {
  agency_principal: 'Agency principal',
  agency_rep: 'Agency rep',
  internal_seller: 'Circdal seller',
  reviewer: 'Circdal reviewer',
  admin: 'Admin',
};

export const LEAD_STAGE: Record<string, [string, Tone]> = {
  new: ['New', 'sky'],
  contacted: ['Contacted', 'outline'],
  qualified: ['Qualified', 'green'],
  converted: ['Converted', 'ink'],
  dead: ['Closed', 'red'],
};

/** Where a lead came from. Set automatically when the lead is created. */
export const LEAD_ORIGIN: Record<string, [string, Tone]> = {
  website: ['Website', 'sky'],
  circdal: ['Circdal', 'green'],
  rep: ['Rep', 'outline'],
};

export const ACTIVITY_KIND: Record<string, string> = {
  call: 'Call',
  meeting: 'Meeting',
  email: 'Email',
  note: 'Note',
  site_visit: 'Site visit',
  system: 'Update',
};

export const SUBMITTAL_STATUS: Record<string, [string, Tone]> = {
  draft: ['Draft', 'outline'],
  submitted: ['Submitted', 'sky'],
  in_design_review: ['In design review', 'sky'],
  changes_requested: ['Changes requested', 'warn'],
  in_pricing_review: ['In pricing review', 'sky'],
  released: ['Released to rep', 'orange'],
  sent_to_client: ['Sent to client', 'ink'],
  approved: ['Client approved', 'green'],
  approved_as_noted: ['Approved as noted', 'green'],
  revise_and_resubmit: ['Revise and resubmit', 'warn'],
  rejected: ['Rejected', 'red'],
  cancelled: ['Cancelled', 'outline'],
};

export const SAMPLE_STATUS: Record<string, [string, Tone]> = {
  awaiting_rep: ['Waiting for rep', 'warn'],
  requested: ['Requested', 'outline'],
  ordered: ['Ordered', 'sky'],
  shipped: ['Shipped', 'orange'],
  delivered: ['Delivered', 'green'],
  cancelled: ['Cancelled', 'outline'],
  failed: ['Order failed', 'red'],
};

export const DOC_CATEGORY: Record<string, string> = {
  spec: 'Specifications',
  installation: 'Installation',
  data_sheet: 'Data sheets',
  sustainability: 'Sustainability',
  warranty: 'Warranty',
  price_book: 'Price book',
  brand: 'Brand assets',
  sales: 'Sales tools',
  other: 'Other',
};

export const PHOTO_STATUS: Record<string, [string, Tone]> = {
  pending: ['Waiting for approval', 'warn'],
  approved: ['Approved', 'green'],
  rejected: ['Not approved', 'red'],
};

export const COMMISSION_STATUS: Record<string, [string, Tone]> = {
  pending: ['Pending', 'outline'],
  partially_earned: ['Partly earned', 'sky'],
  earned: ['Earned', 'green'],
  paid: ['Paid', 'ink'],
  void: ['Void', 'red'],
  none: ['No commission', 'outline'],
};

export const PAYOUT_STATUS: Record<string, [string, Tone]> = {
  draft: ['Draft', 'warn'],
  paid: ['Paid', 'green'],
  void: ['Void', 'red'],
};

export const ORDER_STATUS: Record<string, [string, Tone]> = {
  unpaid: ['Unpaid', 'outline'],
  partially_paid: ['Partly paid', 'sky'],
  paid: ['Paid in full', 'green'],
  void: ['Void', 'red'],
};

/** The shop's order status as the ERP reports it (labels match the API's statusLabel). */
export const ERP_STATUS: Record<string, [string, Tone]> = {
  PENDING: ['Order received', 'outline'],
  CONFIRMED: ['Confirmed', 'sky'],
  IN_PRODUCTION: ['In production', 'orange'],
  PRODUCTION_COMPLETE: ['Production complete', 'orange'],
  PICKING: ['Packing', 'sky'],
  PACKING: ['Packing', 'sky'],
  READY_FOR_PICKUP: ['Ready for pickup', 'green'],
  PARTIALLY_SHIPPED: ['Partly shipped', 'sky'],
  SHIPPED: ['Shipped', 'green'],
  DELIVERED: ['Delivered', 'green'],
  PICKED_UP: ['Picked up', 'green'],
  CANCELLED: ['Cancelled by the shop', 'red'],
};

export const PAYMENT_KIND: Record<string, string> = { deposit: 'Deposit', progress: 'Progress payment', final: 'Final payment', refund: 'Refund' };

export function label(map: Record<string, [string, Tone]> | Record<string, string>, key: string | null | undefined): string {
  if (!key) return '—';
  const v = (map as Record<string, unknown>)[key];
  if (Array.isArray(v)) return v[0] as string;
  return (v as string) ?? key.replace(/_/g, ' ');
}

export function tone(map: Record<string, [string, Tone]>, key: string | null | undefined): Tone {
  return key && map[key] ? map[key]![1] : '';
}

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];
